<script setup>
import { ref, computed, watch } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';
import { formatDate } from './useMonitorHelpers';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
  fromDate: {
    type: Number,
    required: true,
  },
  toDate: {
    type: Number,
    required: true,
  },
  groupBy: {
    type: String,
    default: 'day',
  },
});

const isLoading = ref(false);
const reportData = ref(null);
const activeInsightMetric = ref('follower_count');

const accountInsights = computed(
  () => reportData.value?.account_insights || []
);
const mediaInsightsSummary = computed(
  () => reportData.value?.media_insights_summary || {}
);

const insightsMaxValue = computed(() => {
  if (!accountInsights.value.length) return 0;
  return Math.max(
    ...accountInsights.value.map(d => d[activeInsightMetric.value] || 0)
  );
});

function getInsightBarHeight(value) {
  if (!insightsMaxValue.value) return 0;
  return (value / insightsMaxValue.value) * 100;
}

function getInsightBarColor() {
  if (activeInsightMetric.value === 'impressions') return 'bg-n-amber-9';
  if (activeInsightMetric.value === 'reach') return 'bg-n-teal-9';
  if (activeInsightMetric.value === 'profile_views') return 'bg-n-blue-9';
  return 'bg-n-violet-9';
}

const latestFollowerCount = computed(() => {
  if (!accountInsights.value.length) return 0;
  return (
    accountInsights.value[accountInsights.value.length - 1]?.follower_count || 0
  );
});

const followerGrowth = computed(() => {
  if (accountInsights.value.length < 2) return 0;
  const first = accountInsights.value[0]?.follower_count || 0;
  const last =
    accountInsights.value[accountInsights.value.length - 1]?.follower_count ||
    0;
  return last - first;
});

const totalImpressions = computed(() => {
  return accountInsights.value.reduce(
    (sum, d) => sum + (d.impressions || 0),
    0
  );
});

const totalReach = computed(() => {
  return accountInsights.value.reduce((sum, d) => sum + (d.reach || 0), 0);
});

const totalProfileViews = computed(() => {
  return accountInsights.value.reduce(
    (sum, d) => sum + (d.profile_views || 0),
    0
  );
});

async function fetchReport() {
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getReport({
      from: props.fromDate,
      to: props.toDate,
      inboxId: props.selectedInbox?.id,
      groupBy: props.groupBy,
    });
    reportData.value = response.data;
  } catch {
    // silently fail
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => [props.fromDate, props.toDate, props.selectedInbox, props.groupBy],
  () => fetchReport(),
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <template v-else-if="reportData">
      <!-- Insights Summary Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.FOLLOWERS') }}
          </p>
          <p class="text-2xl font-semibold text-n-violet-11">
            {{ latestFollowerCount.toLocaleString() }}
          </p>
          <p
            v-if="followerGrowth !== 0"
            class="text-xs mt-1"
            :class="followerGrowth > 0 ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ followerGrowth > 0 ? '+' : '' }}{{ followerGrowth }}
            {{ $t('INSTAGRAM_MEDIA_REPORTS.IN_PERIOD') }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.IMPRESSIONS') }}
          </p>
          <p class="text-2xl font-semibold text-n-amber-11">
            {{ totalImpressions.toLocaleString() }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.REACH') }}
          </p>
          <p class="text-2xl font-semibold text-n-teal-11">
            {{ totalReach.toLocaleString() }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.PROFILE_VIEWS') }}
          </p>
          <p class="text-2xl font-semibold text-n-blue-11">
            {{ totalProfileViews.toLocaleString() }}
          </p>
        </div>
      </div>

      <!-- Media Insights Summary -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.MEDIA_IMPRESSIONS') }}
          </p>
          <p class="text-lg font-semibold text-n-amber-11">
            {{
              (mediaInsightsSummary.total_impressions || 0).toLocaleString()
            }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.MEDIA_REACH') }}
          </p>
          <p class="text-lg font-semibold text-n-teal-11">
            {{ (mediaInsightsSummary.total_reach || 0).toLocaleString() }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.MEDIA_SAVED') }}
          </p>
          <p class="text-lg font-semibold text-n-pink-11">
            {{ (mediaInsightsSummary.total_saved || 0).toLocaleString() }}
          </p>
        </div>
        <div
          class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center"
        >
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.MEDIA_SHARES') }}
          </p>
          <p class="text-lg font-semibold text-n-blue-11">
            {{ (mediaInsightsSummary.total_shares || 0).toLocaleString() }}
          </p>
        </div>
      </div>

      <!-- Account Insights Chart -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.INSIGHTS_CHART') }}
          </h3>
          <div class="flex gap-2 flex-wrap">
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeInsightMetric === 'follower_count'
                  ? 'bg-n-violet-9 text-white border-n-violet-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeInsightMetric = 'follower_count'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.FOLLOWERS') }}
            </button>
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeInsightMetric === 'impressions'
                  ? 'bg-n-amber-9 text-white border-n-amber-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeInsightMetric = 'impressions'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.IMPRESSIONS') }}
            </button>
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeInsightMetric === 'reach'
                  ? 'bg-n-teal-9 text-white border-n-teal-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeInsightMetric = 'reach'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.REACH') }}
            </button>
            <button
              class="px-3 py-1 text-xs rounded-lg border transition-colors"
              :class="
                activeInsightMetric === 'profile_views'
                  ? 'bg-n-blue-9 text-white border-n-blue-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-slate-7'
              "
              @click="activeInsightMetric = 'profile_views'"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.PROFILE_VIEWS') }}
            </button>
          </div>
        </div>

        <div v-if="accountInsights.length" class="h-64">
          <div class="flex items-end justify-between h-full gap-1">
            <div
              v-for="item in accountInsights"
              :key="item.date"
              class="flex flex-col items-center flex-1"
            >
              <span class="text-xs text-n-slate-11 mb-1">
                {{ item[activeInsightMetric] || 0 }}
              </span>
              <div
                class="w-full rounded-t transition-all duration-300"
                :class="getInsightBarColor()"
                :style="{
                  height: `${getInsightBarHeight(item[activeInsightMetric] || 0)}%`,
                  minHeight:
                    (item[activeInsightMetric] || 0) > 0 ? '4px' : '0',
                }"
              />
              <span
                class="text-xs text-n-slate-11 mt-2 rotate-45 origin-left whitespace-nowrap"
              >
                {{ formatDate(item.date) }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-n-slate-11"
        >
          {{ $t('INSTAGRAM_MEDIA_REPORTS.NO_INSIGHTS_DATA') }}
        </div>
      </div>
    </template>
  </div>
</template>
